import React from 'react'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useStore } from '@store/StoreProvider'
import { formatMoneyCentsDual } from '@/api/billingApi'
import { t } from '@store/I18nStore'
import {
  daysUntil,
  formatNoticeDate,
  formatNoticeDateTime,
  formatNoticeTime,
  isNoticeSuppressedForEntitlements,
  ruDayWord,
  subscriptionExpiryIso,
} from '@utils/subscriptionNotice'

function isTrialPlan(kind?: string | null): boolean {
  return String(kind ?? '').trim().toUpperCase() === 'TRIAL'
}

export const SubscriptionNoticeBanner: React.FC = observer(() => {
  const { auth, billing } = useStore()
  if (!auth.isAuthenticated) return null
  if (billing.awaitingEmailVerification) return null

  const notice = billing.notice
  if (!notice) return null
  if (isNoticeSuppressedForEntitlements(notice, billing.entitlements)) return null
  if (notice.level === 'warning' && billing.noticeHidden) return null

  const isOwner = billing.canManageBilling
  const isCritical = notice.level === 'critical' || billing.workspaceRestricted
  const expiresAt = notice.expiresAt || subscriptionExpiryIso(billing.entitlements)
  const days = Math.max(1, daysUntil(expiresAt) ?? (notice.code === 'T3' ? 3 : 7))
  const plan = billing.entitlements?.plan
  const planName = plan?.name?.trim() || ''
  const trial = isTrialPlan(plan?.kind)
  const amountCents = plan?.periodAmountCents ?? 0
  const autoRenew = billing.entitlements?.subscription?.autoRenew === true
  const owner = billing.entitlements?.owner
  const dateLabel = formatNoticeDateTime(expiresAt) || formatNoticeDate(expiresAt)
  const datePart = formatNoticeDate(expiresAt)
  const timePart = formatNoticeTime(expiresAt)

  let title: string
  let body: string
  let ctaLabel: string | null = null
  let ctaTo = '/billing'

  if (isCritical) {
    if (isOwner) {
      title = t('billing.subscription.ended_panel_paused')
      body =
        t('billing.restricted.widgets_off_inbox')
      ctaLabel = t('billing.restricted.pay_restore')
    } else {
      title = t('billing.restricted.unavailable')
      const who = [owner?.displayName, owner?.email].filter(Boolean).join(', ')
      const restrictedBody = datePart
        ? t('billing.subscription.ended_on_restricted', { date: datePart })
        : t('billing.restricted.description')
      body = `${restrictedBody} ${t('billing.restricted.contact_admin')}${who ? ` — ${who}` : ''}`
    }
  } else if (isOwner) {
    const subject = trial ? t('billing.trial.title') : planName ? `${t('billing.plan_label')} «${planName}»` : t('billing.plan_label')
    title = subject + ' ' + t('billing.trial.ends_in', { time: days + ' ' + ruDayWord(days) })
    body =
      datePart && timePart
        ? datePart + ' ' + t('time.at_time', { time: timePart }) + ' ' + t('billing.restricted.intake_stops_tail')
        : dateLabel
          ? `${dateLabel} ${t('billing.restricted.intake_stops_tail')}`
          : t('billing.subscription.stop_soon_warning')
    if (autoRenew && amountCents > 0 && datePart) {
      body = t('billing.subscription.auto_charge_notice', { amount: formatMoneyCentsDual(amountCents), date: datePart })
      ctaLabel = null
    } else {
      ctaLabel = t('billing.renewal.renew_plan')
      ctaTo = trial ? '/billing?tab=change' : '/billing?tab=change&renew=1'
      if (amountCents > 0) {
        body = `${body} ${t('billing.invoice.amount_due')}: ${formatMoneyCentsDual(amountCents)}.`
      }
    }
  } else {
    title = t('billing.restricted.suspend_soon')
    const who = [owner?.displayName, owner?.email].filter(Boolean).join(', ')
    body = `${dateLabel ? `${dateLabel} ` : ''}${t('billing.restricted.intake_stops_tail')} ${t('billing.restricted.contact_admin')}${who ? ` (${who})` : ''}`
  }

  const mark = isCritical ? '!' : String(days)

  return (
    <div
      className={`sub-notice-banner ${isCritical ? 'is-critical' : 'is-warning'}`}
      role={isCritical ? 'alert' : 'status'}
      aria-live={isCritical ? 'assertive' : 'polite'}
    >
      <div className="sub-notice-banner-inner">
        <span className="sub-notice-mark" aria-hidden="true">
          {mark}
        </span>
        <div className="sub-notice-copy">
          <div className="sub-notice-title">{title}</div>
          <div className="sub-notice-body">{body}</div>
        </div>
        <div className="sub-notice-actions">
          {isOwner && ctaLabel && (
            <Link to={ctaTo} className="sub-notice-btn sub-notice-btn-solid">
              {ctaLabel}
            </Link>
          )}
          {!isCritical && (
            <button
              type="button"
              className="sub-notice-btn sub-notice-btn-ghost"
              onClick={() => billing.hideWarningNotice()}
            >{t('app.actions.hide')}</button>
          )}
        </div>
      </div>
    </div>
  )
})
